import { Box, Paper, PaperProps, Theme, styled } from "@mui/material";

export interface AnimatedPaperProps extends PaperProps {
  /**
   * Enable animated gradient border
   * @default false
   */
  animate?: boolean;
  /**
   * Animation animationSpeed in milliseconds
   * @default 5000
   */
  animationSpeed?: number;
  /**
   * Border stroke width in pixels
   * @default 4
   */
  animationWidth?: number;
  /**
   * Border stroke width in pixels
   * @default 1
   */
  animationRadius?: number;
}

function createGradientBackground(theme: Theme): string {
  const baseColors = [
    theme.palette.primary.light,
    theme.palette.primary.dark,
    theme.palette.primary.light,
    theme.palette.primary.dark,
  ];

  return `conic-gradient(from var(--angle, 0deg), transparent, ${baseColors.join(", ")})`;
}

const StyledAnimatedPaper = styled(Paper, {
  shouldForwardProp: (prop) =>
    prop !== "animate" &&
    prop !== "animationRadius" &&
    prop !== "animationSpeed" &&
    prop !== "animationWidth",
})<AnimatedPaperProps>(
  ({
    animate,
    animationRadius = 1,
    animationSpeed = 5000,
    animationWidth = 1,
    theme,
  }) => ({
    position: "relative",
    background: animate ? "transparent" : theme.palette.background.paper,
    padding: animate ? animationWidth : 0,

    // Content wrapper with solid background (only if animated)
    ...(animate
      ? {
          "& > .animated-paper-content": {
            position: "relative",
            zIndex: 1,
            background: theme.palette.background.paper,
            borderRadius: `calc(${(theme.shape.borderRadius as number) * animationRadius}px - ${animationWidth}px)`,
            height: "100%",
            width: "100%",
          },
        }
      : {}),

    // Animated gradient borders (only if animate is true)
    ...(animate
      ? {
          "@keyframes spin": {
            "0%": { "--angle": "0deg" },
            "100%": { "--angle": "360deg" },
          },

          // Main gradient border
          "&::after": {
            content: '""',
            position: "absolute",
            left: "50%",
            top: "50%",
            transform: "translate(-50%, -50%)",
            width: `calc(100% + ${animationWidth * 0.25}px)`,
            height: `calc(100% + ${animationWidth * 0.25}px)`,
            borderRadius:
              (theme.shape.borderRadius as number) * animationRadius,
            background: createGradientBackground(theme),
            animation: `spin ${animationSpeed}ms linear infinite`,
            zIndex: 0,
          },

          // Blur effect for glow
          "&::before": {
            content: '""',
            position: "absolute",
            left: "50%",
            top: "50%",
            transform: "translate(-50%, -50%)",
            width: `calc(100% + ${animationWidth * 0.5}px)`,
            height: `calc(100% + ${animationWidth * 0.5}px)`,
            borderRadius:
              (theme.shape.borderRadius as number) * animationRadius,
            background: createGradientBackground(theme),
            animation: `spin ${animationSpeed}ms linear infinite`,
            filter: `blur(${animationWidth * 0.75}px)`,
            opacity: 0.5,
            zIndex: 0,
          },
        }
      : {}),
  }),
);

/**
 * AnimatedPaper component with AI-agent inspired animated gradient border
 *
 * @example
 * ```tsx
 * <AnimatedPaper animate animationSpeed={3000} animationWidth={3}>
 *   <Typography>Content with animated border</Typography>
 * </AnimatedPaper>
 * ```
 */
export const AnimatedPaper: React.FC<AnimatedPaperProps> = ({
  animate = false,
  animationSpeed = 5000,
  animationWidth = 4,
  children,
  ...paperProps
}) => {
  return (
    <StyledAnimatedPaper
      animate={animate}
      animationSpeed={animationSpeed}
      animationWidth={animationWidth}
      {...paperProps}
    >
      {animate ? (
        <Box className="animated-paper-content">{children}</Box>
      ) : (
        children
      )}
    </StyledAnimatedPaper>
  );
};
